/**
 * Tax category enumeration
 * 
 * Defines how different types of income are taxed.
 */
export enum TaxCategory {
  // Portfolio income categories
  QualifiedDividend = 'qualified_dividend',      // Taxed at long-term capital gains rate (0/15/20%)
  OrdinaryDividend = 'ordinary_dividend',        // Taxed as ordinary income
  Interest = 'interest',                         // Taxed as ordinary income (federal + state)
  TreasuryInterest = 'treasury_interest',        // Taxed federally, exempt from state
  MunicipalInterest = 'municipal_interest',      // Tax-exempt (may be subject to AMT)
  OptionPremium = 'option_premium',              // Generally short-term capital gains
  ReturnOfCapital = 'return_of_capital',         // Not taxable (reduces cost basis)
  
  // Capital gains (from realized gains, not income records)
  CapitalGainShort = 'capital_gain_short',       // Short-term capital gains (< 1 year)
  CapitalGainLong = 'capital_gain_long',         // Long-term capital gains (≥ 1 year)
  
  // External income categories
  W2Wages = 'w2_wages',                          // W-2 wages - FICA + ordinary income
  SelfEmploymentIncome = 'self_employment_income', // 1099 income - SE tax + ordinary income
  PrivateStockGainLong = 'private_stock_gain_long', // Private company stock (long-term)
  PrivateStockGainShort = 'private_stock_gain_short', // Private company stock (short-term)
  PassiveIncome = 'passive_income',              // Rental, K-1 - ordinary income
  IsoExerciseSpread = 'iso_exercise_spread',     // ISO exercise spread - AMT preference item only
  Other = 'other',                               // Uncategorized
}

/**
 * How a category is taxed, for grouping income on the Taxes page (WEBF-08).
 * `exempt` is income that is not regular federal taxable income: municipal
 * interest, return of capital, and the ISO exercise spread (an AMT
 * preference item only).
 */
export type TaxClass = 'ordinary' | 'capital_gains_short' | 'capital_gains_long' | 'qualified_dividends' | 'exempt';

/**
 * The one category -> tax-class map (WEBF-08). Exhaustive over `TaxCategory`,
 * so a new enum member does not compile until it is classified. The backend
 * stamps it on every `IncomeTaxBreakdown` row (`taxClass`); both Taxes-page
 * panels group by it.
 *
 * Qualified dividends are their own class (not folded into long-term gains).
 */
export const TAX_CLASS_BY_CATEGORY: Record<TaxCategory, TaxClass> = {
  [TaxCategory.CapitalGainShort]: 'capital_gains_short',
  [TaxCategory.PrivateStockGainShort]: 'capital_gains_short',
  [TaxCategory.OptionPremium]: 'capital_gains_short',
  [TaxCategory.CapitalGainLong]: 'capital_gains_long',
  [TaxCategory.PrivateStockGainLong]: 'capital_gains_long',
  [TaxCategory.QualifiedDividend]: 'qualified_dividends',
  [TaxCategory.MunicipalInterest]: 'exempt',
  [TaxCategory.ReturnOfCapital]: 'exempt',
  // AMT preference only — not regular income (shown with the exempt items, marked "AMT only").
  [TaxCategory.IsoExerciseSpread]: 'exempt',
  [TaxCategory.W2Wages]: 'ordinary',
  [TaxCategory.SelfEmploymentIncome]: 'ordinary',
  [TaxCategory.Interest]: 'ordinary',
  [TaxCategory.TreasuryInterest]: 'ordinary',
  [TaxCategory.OrdinaryDividend]: 'ordinary',
  [TaxCategory.PassiveIncome]: 'ordinary',
  [TaxCategory.Other]: 'ordinary',
};

/**
 * The tax class of a category. An unknown string (a row from an older API)
 * is ordinary.
 */
export function getTaxClass(category: TaxCategory | string | undefined): TaxClass {
  if (category && Object.prototype.hasOwnProperty.call(TAX_CLASS_BY_CATEGORY, category)) {
    return TAX_CLASS_BY_CATEGORY[category as TaxCategory];
  }
  return 'ordinary';
}

/**
 * Get display label for a tax category
 */
export function getTaxCategoryLabel(category: TaxCategory): string {
  const labels: Record<TaxCategory, string> = {
    [TaxCategory.QualifiedDividend]: 'Qualified Dividend',
    [TaxCategory.OrdinaryDividend]: 'Ordinary Dividend',
    [TaxCategory.Interest]: 'Interest Income',
    [TaxCategory.TreasuryInterest]: 'Treasury Interest',
    [TaxCategory.MunicipalInterest]: 'Municipal Bond Interest',
    [TaxCategory.OptionPremium]: 'Option Premium',
    [TaxCategory.ReturnOfCapital]: 'Return of Capital',
    [TaxCategory.CapitalGainShort]: 'Short-Term Capital Gain',
    [TaxCategory.CapitalGainLong]: 'Long-Term Capital Gain',
    [TaxCategory.W2Wages]: 'W-2 Wages & Salary',
    [TaxCategory.SelfEmploymentIncome]: 'Self-Employment Income (1099)',
    [TaxCategory.PrivateStockGainLong]: 'External Capital Gains (Long-Term)',
    [TaxCategory.PrivateStockGainShort]: 'External Capital Gains (Short-Term)',
    [TaxCategory.PassiveIncome]: 'Passive Income (Rental, K-1, etc.)',
    [TaxCategory.IsoExerciseSpread]: 'ISO Exercise Spread (AMT)',
    [TaxCategory.Other]: 'Other Income',
  };
  return labels[category] || category;
}

/**
 * Check if income is taxable at federal level
 */
export function isFederallyTaxable(category: TaxCategory): boolean {
  return category !== TaxCategory.MunicipalInterest &&
         category !== TaxCategory.ReturnOfCapital &&
         category !== TaxCategory.IsoExerciseSpread;
}

/**
 * Check if income is taxable at state level
 */
export function isStateTaxable(category: TaxCategory): boolean {
  return category !== TaxCategory.TreasuryInterest &&
         category !== TaxCategory.ReturnOfCapital &&
         category !== TaxCategory.IsoExerciseSpread;
}

/**
 * Check if income qualifies for preferential tax rates (long-term capital gains)
 */
export function hasPreferentialRate(category: TaxCategory): boolean {
  return category === TaxCategory.QualifiedDividend;
}
